/**
 * Botão de alternância em forma de cápsula, para filtro e seleção de uma opção.
 *
 * Papel: um `<button>` com `aria-pressed`, que troca de contorno para fundo
 * cheio quando `ativo`, em um de três tons — o tom padrão em `brass`, e dois
 * tons de estado para quando a opção escolhida é ela própria um alerta ou uma
 * confirmação.
 *
 * Como usar: quem monta um grupo de opções controla `ativo` pelo próprio
 * estado, e decide sozinho se a escolha é única ou múltipla — o `Chip` não
 * sabe se está dentro de um grupo, e por isso não impõe nenhuma das duas
 * regras. `tom` só importa junto com `ativo`: um `Chip` inativo é sempre o
 * mesmo contorno neutro, qualquer que seja o `tom` passado.
 */
import { type ComponentProps } from 'react';

import { cn } from '@/shared/lib/utils';

interface ChipProps extends Omit<ComponentProps<'button'>, 'type'> {
  /** Se esta opção está selecionada. */
  ativo: boolean;
  /**
   * O tom do fundo quando `ativo`. `padrao` é o `brass` neutro; `perigo` e
   * `sucesso` servem à opção que é ela própria um alerta ou uma confirmação.
   *
   * @default 'padrao'
   */
  tom?: 'padrao' | 'perigo' | 'sucesso';
}

/**
 * Um item de alternância em forma de cápsula.
 *
 * @param ativo - Se esta opção está selecionada; vira o valor de
 *   `aria-pressed` e escolhe entre o contorno neutro e o fundo cheio.
 * @param tom - O tom do fundo quando `ativo`.
 * @param className - Classes de quem chama, aplicadas depois das internas
 *   pelo `cn()` e por isso capazes de sobrescrevê-las.
 * @returns O botão de alternância.
 */
export function Chip({ ativo, tom = 'padrao', className, ...props }: ChipProps) {
  return (
    <button
      type="button"
      aria-pressed={ativo}
      className={cn(
        'rounded-pill border border-linha-forte bg-superficie px-3.5 py-2 font-sans text-[12.5px] text-texto-medio transition-[background-color,color,border-color] duration-150 ease-in-out',
        'hover:border-brass hover:text-brass-light',
        ativo && tom === 'padrao' && 'border-brass bg-brass text-background hover:text-background',
        ativo &&
          tom === 'perigo' &&
          'border-perigo bg-perigo text-background hover:border-perigo hover:text-background',
        ativo &&
          tom === 'sucesso' &&
          'border-sucesso bg-sucesso text-background hover:border-sucesso hover:text-background',
        className,
      )}
      {...props}
    />
  );
}
